import * as React from 'react';
import {AppProps} from 'next/app';
import {ThemeProvider} from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import {CacheProvider, EmotionCache} from '@emotion/react';
import createEmotionCache from '../createEmotionCache';
import theme from "../theme";

import {RouteContextProvider} from "../routes/RouteContext";
import './styles.css';
import '../views/home/components/banner.scss'
import {ApolloProvider} from "@apollo/client";
import {useApollo} from "../store/StoreLink";
import {StaticRouter} from "react-router-dom/server";
import {BrowserRouter} from 'react-router-dom';
import i18n from "i18next";
import {initReactI18next} from "react-i18next";
import translationsEN from "../translations/en.json";
import translationsNL from "../translations/nl.json";
import translationsFR from "../translations/fr.json";
import {useRouter} from "next/router";
import isSSR from '../utils/isSSR';

import {Open_Sans} from '@next/font/google';
import {loadErrorMessages, loadDevMessages} from "@apollo/client/dev";
import {getViewportFromUserAgent, useViewport, ViewportProvider} from "../utils/viewportHelper";
import {PwaContextProvider} from "../components/InstallPwa/PwaContext";
// If loading a variable font, you don't need to specify the font weight
const opensans = Open_Sans({subsets: ['latin']})
// Client-side cache, shared for the whole session of the user in the browser.
const clientSideEmotionCache = createEmotionCache();

interface MyAppProps extends AppProps {
    emotionCache?: EmotionCache;
}

if (process.env.NODE_ENV == "development") {  // Adds messages only in a dev environment
    loadDevMessages();
    loadErrorMessages();
}

const MyApp = (props: MyAppProps) => {
    const {Component, emotionCache = clientSideEmotionCache, pageProps} = props;
    const router = useRouter();
    const apolloClient = useApollo(pageProps)
    const viewPort = getViewportFromUserAgent(useViewport(pageProps));


    if (isSSR())
        return (
                <CacheProvider value={emotionCache}>
                    <ViewportProvider initialViewport={viewPort}>
                        <ApolloProvider client={apolloClient}>
                            <StaticRouter location={'/' + router.locale + (router.asPath == '/' ? '' : router.asPath)}>
                                <RouteContextProvider>
                                    <PwaContextProvider>
                                        <ThemeProvider theme={theme}>
                                            {/* CssBaseline kickstart an elegant, consistent, and simple baseline to build upon. */}
                                            <CssBaseline/>
                                            <main className={opensans.className}>
                                                <Component {...pageProps} />
                                            </main>
                                        </ThemeProvider>
                                    </PwaContextProvider>
                                </RouteContextProvider>
                            </StaticRouter>
                        </ApolloProvider>
                    </ViewportProvider>
                </CacheProvider>
        );

    i18n.use(initReactI18next)
        .init(
            {
                lng: router.locale,
                resources: {
                    en: {translation: translationsEN},
                    nl: {translation: translationsNL},
                    fr: {translation: translationsFR}
                },
                fallbackLng: "en",
                supportedLngs: ['en', 'nl', 'fr'],
                interpolation: {
                    "escapeValue": false
                },
                nonExplicitSupportedLngs: false
            }
        );

    return (
            <CacheProvider value={emotionCache}>
                <ViewportProvider initialViewport={viewPort}>
                    <ApolloProvider client={apolloClient}>
                        <BrowserRouter>
                            <RouteContextProvider>
                                <PwaContextProvider>
                                    <ThemeProvider theme={theme}>
                                        {/* CssBaseline kickstart an elegant, consistent, and simple baseline to build upon. */}
                                        <CssBaseline/>
                                        <main className={opensans.className}>
                                            <Component {...pageProps} />
                                        </main>
                                    </ThemeProvider>
                                </PwaContextProvider>
                            </RouteContextProvider>
                        </BrowserRouter>
                    </ApolloProvider>
                </ViewportProvider>
            </CacheProvider>
    );
}

export default MyApp;